import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ShieldCheck, Lock, Mail, Cookie, Database, UserCheck, AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy & Trust | Creed Car Rental" },
      { name: "description", content: "How Creed Car Rental collects, uses, and protects your personal information — plus the security and trust practices we follow." },
      { property: "og:title", content: "Privacy & Trust | Creed Car Rental" },
      { property: "og:description", content: "Our privacy policy, data handling, and trust practices." },
      { property: "og:url", content: "https://creedcarental.com/privacy" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://creedcarental.com/privacy" }],
  }),
  component: PrivacyPage,
});

const LAST_UPDATED = "June 19, 2026";

function PrivacyPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <article className="mx-auto max-w-3xl px-6 py-16">
        <header className="border-b border-border pb-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-accent/10 px-3 py-1 text-xs font-medium text-accent">
            <ShieldCheck className="h-3.5 w-3.5" />
            Privacy & Trust
          </div>
          <h1 className="mt-4 font-display text-4xl tracking-tight text-foreground md:text-5xl">
            Your privacy, plainly explained
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Last updated {LAST_UPDATED}. This page is maintained by Creed Car Rental to
            answer common privacy, security, and trust questions about our service.
          </p>
        </header>

        <div className="prose prose-neutral mt-10 max-w-none [&_h2]:mt-10 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:text-foreground [&_h3]:text-foreground [&_p]:text-foreground/80 [&_li]:text-foreground/80 [&_a]:text-accent">
          <Section icon={<Database className="h-5 w-5" />} title="Information we collect">
            <p>
              We collect only what we need to process a rental booking and provide
              support:
            </p>
            <ul>
              <li><strong>Booking details</strong> — name, email, phone, pickup &amp; return dates, delivery location, and the vehicle selected.</li>
              <li><strong>Driver verification</strong> — driver's license image and proof of insurance, required by Florida law and our insurer.</li>
              <li><strong>Payment information</strong> — processed by our payment provider (Stripe). We do <em>not</em> store full card numbers on our servers.</li>
              <li><strong>Usage data</strong> — anonymized page views and interactions via Google Analytics 4 and Microsoft Clarity to improve the site.</li>
            </ul>
          </Section>

          <Section icon={<UserCheck className="h-5 w-5" />} title="How we use it">
            <ul>
              <li>To confirm bookings, deliver vehicles, and provide customer support.</li>
              <li>To verify driver eligibility and meet insurance requirements.</li>
              <li>To send transactional emails (booking confirmations, reminders, receipts). You can unsubscribe from non-essential emails anytime via the link in any email footer.</li>
              <li>To improve our website &amp; fleet based on aggregated usage patterns.</li>
            </ul>
            <p>We do <strong>not</strong> sell your personal information, ever.</p>
          </Section>

          <Section icon={<Lock className="h-5 w-5" />} title="How we protect it">
            <ul>
              <li>All traffic is served over HTTPS/TLS.</li>
              <li>Payments are processed by Stripe (PCI-DSS Level 1).</li>
              <li>Driver's license images are stored in access-restricted cloud storage and viewed only by authorized staff during the verification window.</li>
              <li>Database access is gated by row-level security policies — booking data is visible only to the customer who created it and to authorized admins.</li>
              <li>Admin accounts use email-and-password authentication with role-based access; no shared logins.</li>
            </ul>
          </Section>

          <Section icon={<Cookie className="h-5 w-5" />} title="Cookies & analytics">
            <p>We use a small number of cookies:</p>
            <ul>
              <li><strong>Essential</strong> — session and authentication cookies required to use the site and complete a booking.</li>
              <li><strong>Analytics</strong> — Google Analytics 4 and Microsoft Clarity track aggregate, anonymized usage. You can opt out via your browser settings or add-ons such as the Google Analytics Opt-out.</li>
            </ul>
          </Section>

          <Section icon={<ShieldCheck className="h-5 w-5" />} title="Third-party services">
            <p>We share data only with the providers needed to operate the service:</p>
            <ul>
              <li><strong>Stripe</strong> — payment processing.</li>
              <li><strong>Resend</strong> — transactional email delivery.</li>
              <li><strong>Google Analytics &amp; Microsoft Clarity</strong> — anonymized analytics.</li>
              <li><strong>Lovable Cloud</strong> — application hosting, database, authentication, and file storage.</li>
            </ul>
          </Section>

          <Section icon={<UserCheck className="h-5 w-5" />} title="Your rights">
            <p>You can request to:</p>
            <ul>
              <li>Access a copy of the personal data we hold about you.</li>
              <li>Correct inaccurate information.</li>
              <li>Delete your data, subject to legal retention requirements for completed rental transactions.</li>
              <li>Opt out of marketing email.</li>
            </ul>
            <p>
              To exercise any of these, email{" "}
              <a href="mailto:Creedcarental@gmail.com">Creedcarental@gmail.com</a>{" "}
              and we'll respond within 30 days.
            </p>
          </Section>

          <Section icon={<AlertTriangle className="h-5 w-5" />} title="Retention">
            <p>
              Booking records are retained for the period required by Florida tax and
              insurance law (typically 7 years). Driver's license images are retained
              only for the duration of the rental plus a short post-rental window for
              incident review, then deleted.
            </p>
          </Section>

          <Section icon={<Mail className="h-5 w-5" />} title="Contact">
            <p>
              Questions about privacy, security, or this page? Reach us at{" "}
              <a href="mailto:Creedcarental@gmail.com">Creedcarental@gmail.com</a>{" "}
              or <a href="tel:+13055209765">+1 (305) 520-9765</a>.
            </p>
            <p className="text-sm text-muted-foreground">
              This page is informational and is not a certification or independent
              audit. Creed Car Rental is responsible for the practices described
              above; our hosting and payment partners are responsible for the
              security of their respective platforms.
            </p>
          </Section>

          <div className="mt-12 border-t border-border pt-6 text-sm">
            <Link to="/" className="text-accent hover:underline">← Back to home</Link>
          </div>
        </div>
      </article>
      <SiteFooter />
    </div>
  );
}

function Section({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="flex items-center gap-2">
        <span className="text-accent">{icon}</span>
        {title}
      </h2>
      {children}
    </section>
  );
}
